import React, { useEffect, useRef, useState, Suspense } from "react";
import { Maximize2, Download } from "lucide-react";
import {
  downloadSvg,
  generateDiagramFilename,
} from "../../../utils/diagramExport";
import { renderMermaidSvg } from "../../../utils/mermaidConfig";
import {
  reportInvalidMermaidOutput,
  reportMermaidThrown,
} from "../../../utils/mermaidReporting";
import { useDiagramContext } from "../../../hooks/useDiagramContext";

const MermaidDiagramModal = React.lazy(() => import("./MermaidDiagramModal"));

const preloadModal = () => void import("./MermaidDiagramModal");

const toolbarButtonClass =
  "rounded-md bg-white/90 p-1.5 text-slate-600 shadow-sm ring-1 ring-slate-200 hover:bg-white hover:text-slate-900 transition-colors";

/**
 * Renders a single Mermaid diagram as a self-contained React component.
 * Manages its own ref so re-renders of the parent don't wipe the SVG.
 */
export function MermaidDiagram({ source }: { source: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<string>("");
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { clientInitials } = useDiagramContext();

  useEffect(() => {
    if (!containerRef.current || !source) return;
    let cancelled = false;

    const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    renderMermaidSvg(id, source)
      .then((svg) => {
        reportInvalidMermaidOutput("chat", svg, false);
        if (!cancelled && containerRef.current) {
          svgRef.current = svg;
          containerRef.current.innerHTML = svg;
        }
      })
      .catch((error: unknown) => {
        reportMermaidThrown("chat", error);
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [source]);

  if (error) {
    return (
      <pre className="my-1 text-xs">
        <code>{source}</code>
      </pre>
    );
  }

  return (
    <>
      <div className="group relative my-2">
        <div ref={containerRef} className="mermaid-diagram overflow-x-auto" />
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() =>
              downloadSvg(
                svgRef.current,
                generateDiagramFilename("svg", clientInitials)
              )
            }
            className={toolbarButtonClass}
            title="Download SVG"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onMouseEnter={preloadModal}
            onClick={() => setIsModalOpen(true)}
            className={toolbarButtonClass}
            title="View fullscreen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {isModalOpen && (
        <Suspense fallback={null}>
          <MermaidDiagramModal
            svgString={svgRef.current}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            clientInitials={clientInitials}
          />
        </Suspense>
      )}
    </>
  );
}
